import React, { useState } from 'react';
import { Mail, Send, X, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailSendModal({ docType, docId, docNumber, clientName, clientEmail, authHeaders, onClose, onSent }) {
  const [email, setEmail] = useState(clientEmail || '');
  const [name, setName] = useState(clientName || '');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const typeLabel = docType === 'quote' ? 'devis' : 'facture';
  const endpoint = docType === 'quote' ? 'quotes' : 'invoices';

  const handleSend = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/api/crm/${endpoint}/${docId}/send-email`, {
        recipient_email: email,
        recipient_name: name,
        message,
      }, { headers: authHeaders });
      setSent(true);
      onSent?.();
    } catch (err) {
      setError(err.response?.data?.detail || `Erreur lors de l'envoi de l'email`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[55] flex items-center justify-center p-4" data-testid="email-send-modal">
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Mail className="text-blue-600" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Envoyer par email</h3>
              <p className="text-xs text-gray-500">{docNumber}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="close-email-modal">
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        {sent ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="text-green-600" size={32} />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Email envoyé !</h4>
            <p className="text-sm text-gray-600 mb-6">
              Le {typeLabel} <strong>{docNumber}</strong> a été envoyé avec succès à <strong>{email}</strong>
            </p>
            <button onClick={onClose} className="px-6 py-2.5 bg-[#DC2626] text-white rounded-lg hover:bg-red-700 text-sm font-medium" data-testid="email-sent-close">
              Fermer
            </button>
          </div>
        ) : (
          <form onSubmit={handleSend} className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email du destinataire *</label>
              <input
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="client@example.com"
                className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="email-recipient-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom du destinataire</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Nom du client"
                className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="email-recipient-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message personnalisé (optionnel)</label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder={`Suite à notre échange, veuillez trouver ci-joint votre ${typeLabel}.`}
                rows={3}
                className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                data-testid="email-message-input"
              />
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
              <p className="text-xs text-blue-700">
                <strong>Pièce jointe :</strong> Le PDF du {typeLabel} {docNumber} sera automatiquement joint à l'email.
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="email-error">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
                Annuler
              </button>
              <button
                type="submit"
                disabled={sending || !email}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#DC2626] text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="send-email-btn"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {sending ? 'Envoi en cours...' : 'Envoyer'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
